/**
 * Print resolutions a bottle label can be *printed* at.
 *
 * ZPL positions everything in printer dots, so a template only renders at its intended physical
 * size on the resolution it was drawn for. Every entry here has a template variant behind it — a
 * built-in default at minimum — so choosing one always yields a correctly sized label.
 *
 * Must stay in step with `LabelTemplate.SupportedDpis` on the server, which rejects a print request
 * naming anything outside this set.
 */
export const SUPPORTED_LABEL_DPIS = [203, 300] as const;

/**
 * Resolutions a template can be *authored* against in the template tester.
 *
 * Deliberately wider than {@link SUPPORTED_LABEL_DPIS}: an administrator can draw and preview a
 * template for a printer the filling screen cannot yet select, because Labelary renders the preview
 * and only accepts resolutions expressed in whole dots per millimetre. These four line up with the
 * DPI of the Zebra printers labels are produced on and each converts cleanly — 152/203/300/600 DPI
 * being 6/8/12/24 dpmm.
 */
export const AUTHORABLE_LABEL_DPIS = [152, 203, 300, 600] as const;

/**
 * Resolution assumed when nobody has chosen one — both for an operator who never touches the
 * filling-screen selector and for a newly created template. Matches `LabelTemplate.DefaultDpi` on
 * the server.
 */
export const DEFAULT_LABEL_DPI = 300;

export type LabelDpi = (typeof SUPPORTED_LABEL_DPIS)[number];

/** Whole dots per millimetre, the unit Labelary expects for preview rendering. */
export const toDpmm = (dpi: number): number => Math.round(dpi / 25.4);

export function isSupportedLabelDpi(dpi: unknown): dpi is LabelDpi {
    return (SUPPORTED_LABEL_DPIS as readonly unknown[]).includes(dpi);
}

export function isAuthorableLabelDpi(dpi: unknown): boolean {
    return (AUTHORABLE_LABEL_DPIS as readonly unknown[]).includes(dpi);
}

/**
 * The printable resolutions written for an administrator to read, e.g. "203 and 300".
 *
 * Derived from {@link SUPPORTED_LABEL_DPIS} rather than written out, so a resolution added there
 * cannot be left out of the notice that tells a template author which resolutions reach a printer.
 */
export function printableLabelDpiList(): string {
    const dpis = [...SUPPORTED_LABEL_DPIS];
    if (dpis.length < 2) return String(dpis[0] ?? "");
    return `${dpis.slice(0, -1).join(", ")} and ${dpis[dpis.length - 1]}`;
}

/**
 * Coerces a persisted or user-supplied value to a printable resolution, so a stale localStorage
 * entry or a hand-edited value cannot put the page into a state the server will reject.
 */
export function coerceLabelDpi(dpi: unknown): number {
    const parsed = Number(dpi);
    return isSupportedLabelDpi(parsed) ? parsed : DEFAULT_LABEL_DPI;
}

/** Options for the template tester's authoring-resolution picker. */
export const authorableDpiSelectOptions = AUTHORABLE_LABEL_DPIS.map(d => ({
    value: d,
    text: `${d} DPI (${toDpmm(d)} dpmm)`
}));
